// { nx, ny, mask, frames: [ … ] } continuous { key, label, kind:'continuous', ramp, min, max, plane
// } categorical { key, label, kind:'categorical', categories, plane } byte = index, most-common
// first rgb channel { key, label, kind:'continuous', ramp:'_raw8', rgb_group, rgb_channel, min
// max, plane } `mask` AND NODATA ARE DIFFERENT ENCODINGS, and this is the one to get right mask
// bytes are 0/1 — 1 = inside the field plane bytes are 0–254, with 255 reserved for "no value here"
// A `mask == 0` cell also reads 255 in every frame, so **255 alone cannot tell "outside the field"
// from "a hole in the data"**. Outside is not drawn at all; inside-but-255 is drawn as a HOLE
// because painting a hole as though the field stopped there is a picture of something untrue.
import type { AnimCell } from "./mapSoilAnimation";

/* * Inside the field, but the layer has no reading here. Deliberately NOT a ramp colour. */
export const HOLE_COLOR = "#1e293b";

export interface AnimFrame {
  key?: string;
  label?: string;
  kind?: string;
  ramp?: string;
  min?: number;
  max?: number;
  /*
   *
   * Nothing here re-stretches a plane, so this is documentation of an invariant rather than a
   * switch — which is the point: the guarantee lives in the data, not in a convention both sides
   * have to remember.
   */
  absolute_domain?: boolean;
  /* * for `kind: 'bits'` — what each bit position means, low bit first */
  bit_labels?: string[];
  categories?: string[];
  rgb_group?: string;
  rgb_channel?: string;
  plane?: string;
}

export interface AnimFramePayload {
  nx?: number;
  ny?: number;
  mask?: string;
  frames?: AnimFrame[];
}

/* * base64 → bytes. Returns null rather than throwing on anything malformed. */
export function decodePlane(b64: string | undefined): Uint8Array | null {
  if (!b64) return null;
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

// An unknown name still falls back rather than failing — the ramp is decoration on decoration, and
// a name we don't know yet must not cost the user a frame — but the table now covers everything
// compute actually sends — their manifest.
const RAMPS: Record<string, string[]> = {
  blues: ["#f7fbff", "#c6dbef", "#6baed6", "#2171b5", "#08306b"],
  bugn: ["#f7fcfd", "#ccece6", "#66c2a4", "#238b45", "#00441b"],
  bupu: ["#f7fcfd", "#bfd3e6", "#8c96c6", "#88419d", "#4d004b"],
  greens: ["#f7fcf5", "#c7e9c0", "#74c476", "#238b45", "#00441b"],
  oranges: ["#fff5eb", "#fdd0a2", "#fd8d3c", "#d94801", "#7f2704"],
  orrd: ["#fff7ec", "#fdd49e", "#fc8d59", "#d7301f", "#7f0000"],
  pubu: ["#fff7fb", "#d0d1e6", "#74a9cf", "#0570b0", "#023858"],
  purples: ["#fcfbfd", "#dadaeb", "#9e9ac8", "#6a51a3", "#3f007d"],
  rdbu: ["#0571b0", "#92c5de", "#f7f7f7", "#f4a582", "#ca0020"],
  rdylgn: ["#a50026", "#f46d43", "#fee08b", "#a6d96a", "#1a9850"],
  ylgn: ["#ffffe5", "#d9f0a3", "#78c679", "#238443", "#004529"],
  ylorbr: ["#ffffe5", "#fee391", "#fe9929", "#cc4c02", "#662506"],
  copper: ["#000000", "#5a3a25", "#a06a45", "#d99b6a", "#ffc78f"],
  inferno: ["#000004", "#57106e", "#bc3754", "#f98e09", "#fcffa4"],
  magma: ["#000004", "#51127c", "#b73779", "#fc8961", "#fcfdbf"],
  terrain: ["#2a7fbf", "#3fbf7f", "#cfe08d", "#a68a5b", "#ffffff"],
  viridis: ["#440154", "#3b528b", "#21918c", "#5ec962", "#fde725"],
};
const DEFAULT_RAMP = RAMPS.viridis!;

/* * Compute's sentinel for "this is a colour channel, do NOT apply a ramp". */
export const RAW8 = "_raw8";

/*
 *
 * Water table depth is reversed **on purpose**: larger is deeper is drier, so rendering it forward
 * tells the grower the wet end of the field is the dry end. That is not a cosmetic miss; it is the
 * layer saying the opposite of what it means.
 */
export function rampFor(name: string | undefined): string[] {
  const raw = (name ?? "").toLowerCase();
  const reversed = /_r$/.test(raw);
  const k = raw.replace(/_r$/, "").replace(/[^a-z0-9]/g, "");
  const base = RAMPS[k] ?? DEFAULT_RAMP;
  return reversed ? [...base].reverse() : base;
}

/* * The Classic-Grid overlay's two states: still available, or excluded by a rule. */
export const EXCLUSION_CLEAR = "#22c55e";
export const EXCLUSION_DARK = "#0b1220";

/* * Categorical colours — distinct hues, cycling. The byte is an index, most-common first. */
const CATEGORY_COLORS = [
  "#4e79a7",
  "#f28e2b",
  "#59a14f",
  "#e15759",
  "#b07aa1",
  "#76b7b2",
  "#edc948",
  "#ff9da7",
  "#9c755f",
  "#bab0ac",
];

function hex(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, "0");
}

/*
 *
 * Returns `null` when the payload can't be trusted — a missing size, a plane whose length disagrees
 * with `nx*ny`, or nothing left after dropping bad frames.
 */
export function cellsFromFrames(payload: AnimFramePayload | null | undefined): {
  cells: AnimCell[];
  labels: string[];
  keys: string[];
  bitLabels: (string[] | null)[];
  ramps: (string[] | null)[];
  aspect: number;
} | null {
  const nx = payload?.nx;
  const ny = payload?.ny;
  if (!nx || !ny || nx < 1 || ny < 1) return null;
  const n = nx * ny;
  const mask = decodePlane(payload?.mask);
  if (mask && mask.length !== n) return null;

  // Group the RGB channels back together. They arrive as three consecutive frames sharing
  // `rgb_group`; without that grouping they would animate as three unrelated covariates.
  const groups = new Map<string, AnimFrame[]>();
  const ordered: {
    key: string;
    label: string;
    bits?: string[];
    stops?: string[];
    paint: (i: number) => string | null;
    value: (i: number) => number | null;
  }[] = [];
  const seenGroup = new Set<string>();

  for (const f of payload?.frames ?? []) {
    const plane = decodePlane(f.plane);
    if (!plane || plane.length !== n) continue; // drop rather than render skewed
    if (f.rgb_group) {
      const g = groups.get(f.rgb_group) ?? [];
      g.push({ ...f, plane: f.plane });
      groups.set(f.rgb_group, g);
      if (seenGroup.has(f.rgb_group)) continue;
      seenGroup.add(f.rgb_group);
      const label = f.label ?? f.rgb_group;
      ordered.push({
        key: f.rgb_group,
        // an RGB composite has three numbers, not one — there is no single value to report, and
        // inventing one (a luminance, say) would be a number nobody asked for and nobody can check
        value: () => null,
        label,
        paint: (i) => {
          const chans = groups.get(f.rgb_group!) ?? [];
          const byCh: Record<
            string,
            { p: Uint8Array; min: number; max: number }
          > = {};
          for (const c of chans) {
            const p = decodePlane(c.plane);
            if (p)
              byCh[c.rgb_channel ?? ""] = {
                p,
                min: c.min ?? 0,
                max: c.max ?? 255,
              };
          }
          const r = byCh.r,
            g2 = byCh.g,
            b = byCh.b;
          if (!r || !g2 || !b) return null;
          if (r.p[i] === 255 || g2.p[i] === 255 || b.p[i] === 255) return null;
          // The ranges are per channel and per field, so they are not interchangeable — and `_raw8`
          // means "this is a colour channel, do not apply a ramp".
          const v = (c: { p: Uint8Array; min: number; max: number }) =>
            c.min + (c.p[i]! / 254) * (c.max - c.min);
          return `#${hex(v(r))}${hex(v(g2))}${hex(v(b))}`;
        },
      });
      continue;
    }
    if (f.kind === "categorical") {
      const cats = f.categories ?? [];
      ordered.push({
        key: f.key ?? "",
        label: f.label ?? f.key ?? "",
        paint: (i) => {
          const b = plane[i]!;
          if (b === 255 || b >= Math.max(1, cats.length)) return null;
          return CATEGORY_COLORS[b % CATEGORY_COLORS.length]!;
        },
        // a category's INDEX is its value; there is no continuous scale to ride
        value: (i) => (plane[i] === 255 ? null : plane[i]!),
      });
      continue;
    }
    if (f.kind === "bits") {
      // The byte IS the answer — bit i means rule i fired here so it must never ride the min/max
      // dequantisation. Compute pins these bits to OR back to the exact mask that moved the real
      // grid points, which is the whole reason we asked for the plane instead of re-thresholding
      // the layers ourselves.
      ordered.push({
        key: f.key ?? "",
        label: f.label ?? f.key ?? "",
        bits: f.bit_labels ?? [],
        paint: (i) => {
          const b2 = plane[i]!;
          if (b2 === 255) return null;
          return b2 === 0 ? EXCLUSION_CLEAR : EXCLUSION_DARK;
        },
        value: (i) => (plane[i] === 255 ? null : plane[i]!),
      });
      continue;
    }
    const stops = rampFor(f.ramp);
    const lo = typeof f.min === "number" ? f.min : 0;
    const hi = typeof f.max === "number" ? f.max : 1;
    ordered.push({
      key: f.key ?? "",
      label: f.label ?? f.key ?? "",
      // The Behavior animation has to paint a DECILE — a value the cell does not carry a colour for
      // and the only honest way to colour it is the same ramp the percentile used.
      stops,
      paint: (i) => {
        const b = plane[i]!;
        if (b === 255) return null;
        return stops[
          Math.min(stops.length - 1, Math.round((b / 254) * (stops.length - 1)))
        ]!;
      },
      // the dequantisation compute documents: 255 is NODATA, everything else rides min..max
      value: (i) => {
        const b = plane[i]!;
        return b === 255 ? null : lo + (b / 254) * (hi - lo);
      },
    });
  }

  if (!ordered.length) return null;

  const cells: AnimCell[] = [];
  for (let row = 0; row < ny; row++) {
    for (let col = 0; col < nx; col++) {
      const i = row * nx + col;
      // Only `mask` can say so — a frame's 255 means "outside OR no reading", and conflating them
      // paints a data hole as though the field ended.
      if (mask && mask[i] !== 1) continue;
      cells.push({
        // cell CENTRES, so the tiles sit on the raster rather than half a cell off it
        x: (col + 0.5) / nx,
        y: (row + 0.5) / ny,
        colors: ordered.map((o) => o.paint(i) ?? HOLE_COLOR),
        values: ordered.map((o) => o.value(i)),
      });
    }
  }
  if (!cells.length) return null;
  return {
    cells,
    labels: ordered.map((o) => o.label),
    // THE SERVED KEYS, so a caller can ask for a plane BY NAME instead of by position. The three
    // new animations each need specific layers (a percentile to bin into deciles, the exclusion
    // rule bits, one input per panel), and position is not a name — a field that legitimately drops
    // a plane it has no data for would silently shift every index after it.
    keys: ordered.map((o) => o.key),
    // what each bit position means, for the planes that carry bits; null for everything else
    bitLabels: ordered.map((o) => o.bits ?? null),
    ramps: ordered.map((o) => o.stops ?? null),
    aspect: nx / ny,
  };
}

/*
 *
 * `inline` is the frame object when the manifest includes it. A signed URL
 * carries the same JSON when `inline` is absent. The fetch is a bare GET: the
 * signed URL is the capability, so it takes no API credential. A fetch that
 * fails leaves the animation absent, the same outcome as a missing row.
 */
export async function cellsFromFrameSource(
  source: { inline?: unknown; signed_url?: string | null } | null | undefined,
): Promise<ReturnType<typeof cellsFromFrames>> {
  if (!source) return null;
  const inline = source.inline as AnimFramePayload | null | undefined;
  const payload =
    inline ??
    (source.signed_url
      ? await fetch(source.signed_url)
          .then(async (r) =>
            r.ok ? ((await r.json()) as AnimFramePayload) : null,
          )
          .catch(() => null)
      : null);
  return cellsFromFrames(payload);
}
